import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import "./verify-form.scss";
import { Input } from "antd";
import request from "../../../utils/useApi";
import { useDispatch } from "react-redux";
import { setUser } from "../../../Slices/User/userDataSlice";
import { toast } from "react-toastify";
import { authErrorToMessage } from "../errorMessages";
import { useNavigate } from "react-router-dom";
import { setCurrency } from "../../../utils/context";

const VerifyForm = ({verifyInfo, onLoginSuccess, verifyOnSucess = null}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userId, email, password } = verifyInfo || location.state || {};
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const maskedEmail = (() => {
    const [name = "", domain = ""] = String(email || "").split("@");
    if (!domain) return "your email";
    const visible = name.slice(0, Math.min(2, name.length));
    return `${visible}${"•".repeat(Math.max(3, name.length - visible.length))}@${domain}`;
  })();

  useEffect(() => {
    if (!userId || !email || !password) {
      navigate("/register", { replace: true });
    }
  }, [userId, email, password, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string().required("Please enter the OTP")
      .matches(/^\d{6}$/, "OTP must be 6 digits")
    }),
    onSubmit: async (values) => {
      const otp = values.otp;
      setSubmitting(true);
      try {
        let response = await request.post('/auth/verifyOtp', {
          'userId': userId,
          'otp': otp,
        });

        if (response.status === 200) {
          if (typeof verifyOnSucess === "function") {
            verifyOnSucess();
          } else {
            await login();
          }
        }
      } catch (error) {
        console.error(" error:", error);

        const apiMsg = error?.response?.data?.message;
        const fbMsg = authErrorToMessage(error?.code);

        toast.error(apiMsg || fbMsg || "Login failed. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const login = async () => {
    try {
      let response = await request.post("/auth/login", {
        email: email,
        password: password,
      });

      if (response.status === 200) {
        dispatch(setUser(response.data.data));
        localStorage.setItem("teleport_token", response.data.data.token);
        const currency = response.data.data.user.currency || 'USD';
        setCurrency(currency)
        onLoginSuccess?.();
        toast.success("Logged in successfully!");
      }
    } catch (error) {
      console.error("Login error:", error);

      const apiMsg = error?.response?.data?.message;
      const fbMsg = authErrorToMessage(error?.code);

      toast.error(apiMsg || fbMsg || "Login failed. Please try again.");
    }
  }

  const resendOtp = async () => {
    if (resending || resendCooldown > 0) return;
    setResending(true);
    try {
      let response = await request.post("/auth/resendOTP", {
        email: email,
      });

      if (response.status === 200) {
        setResendCooldown(30);
        toast.success("A new verification code has been sent.");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);

      const apiMsg = error?.response?.data?.message;
      const fbMsg = authErrorToMessage(error?.code);

      toast.error(apiMsg || fbMsg || "Resend OTP failed. Please try again.");
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="verify-section">
      <form onSubmit={formik.handleSubmit} className="verify-form">
        <div className="verify-form__step">Step 2 of 2</div>
        <div className="heading">
          <h2>Check your email</h2>
          <p>Enter the six-digit verification code sent to <strong>{maskedEmail}</strong>.</p>
        </div>

        <div className="otp-inputs">
          <Input.OTP
            name="otp"
            length={6}
            value={formik.values.otp}
            onChange={(val) => formik.setFieldValue("otp", val)}
            onBlur={() => formik.setFieldTouched("otp", true)}
            formatter={(str) => str.replace(/\D/g, "")}
            size="large"
          />
        </div>
        <div className="otp-inputs">
          {formik.touched.otp && formik.errors.otp && (
            <div className="error">{formik.errors.otp}</div>
          )}
        </div>

        <button className="custom-btn" type="submit" disabled={submitting || formik.values.otp.length !== 6}>
          {submitting ? "Verifying…" : "Verify account"}
        </button>

        <p className="verify-form__resend">
          Didn&apos;t receive the code?{" "}
          <button type="button" onClick={resendOtp} disabled={resending || resendCooldown > 0}>
            {resending ? "Sending…" : resendCooldown > 0 ? `Send again in ${resendCooldown}s` : "Send a new code"}
          </button>
        </p>
      </form>
    </div>
  );
};

export default VerifyForm;
